import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { sendsService } from '../services/sends';
import { gymsService } from '../services/gyms';
import { Skeleton } from '../components/ui/skeleton';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '../components/ui/toggle-group';
import GymUserStats from '../components/stats/GymUserStats';
import AllGymsStats from '../components/stats/AllGymsStats';
import type { Send } from '../types/send';
import type { Gym } from '../types/gym';

const ALL_GYMS_ID = '__all__';

interface LocationOption {
  id: string;
  name: string;
  slug: string;
}

export default function MyStats() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [allSends, setAllSends] = useState<Send[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocationId, setSelectedLocationId] = useState<string>(ALL_GYMS_ID);
  const [climbType, setClimbType] = useState<'Boulder' | 'Route'>('Boulder');
  const [gym, setGym] = useState<Gym | null>(null);
  const [gymLoading, setGymLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useBreadcrumb(
    () => ({ onBack: () => navigate(-1), segments: [{ label: t('stats.pageTitle') }] }),
    [t, navigate]
  );

  useEffect(() => {
    if (!user) return;
    sendsService.getUserSends(user.slug)
      .then(res => setAllSends(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const locationOptions = useMemo<LocationOption[]>(() => {
    const seen = new Map<string, LocationOption>();
    for (const send of allSends) {
      const loc = send.attributes.climb.location;
      if (loc && !seen.has(loc.id)) {
        seen.set(loc.id, { id: loc.id, name: loc.name, slug: loc.slug });
      }
    }
    const gyms = Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
    return [{ id: ALL_GYMS_ID, name: t('stats.allGyms'), slug: ALL_GYMS_ID }, ...gyms];
  }, [allSends, t]);

  useEffect(() => {
    if (selectedLocationId === ALL_GYMS_ID) {
      setGym(null);
      return;
    }
    const loc = locationOptions.find(l => l.id === selectedLocationId);
    if (!loc || loc.id === ALL_GYMS_ID) return;
    setGymLoading(true);
    setGym(null);
    gymsService.getGymBySlug(loc.slug)
      .then(g => setGym(g))
      .catch(() => {})
      .finally(() => setGymLoading(false));
  }, [selectedLocationId, locationOptions]);

  const gradingSystem = gym
    ? (climbType === 'Boulder' ? gym.boulder_grading_system : gym.route_grading_system)
    : null;

  const availableTypes = useMemo<Array<'Boulder' | 'Route'>>(() => {
    const sendsToCheck = selectedLocationId === ALL_GYMS_ID
      ? allSends
      : allSends.filter(s => s.attributes.climb.location?.id === selectedLocationId);
    const types = new Set<'Boulder' | 'Route'>();
    for (const send of sendsToCheck) {
      if (send.attributes.climb.climb_type) {
        types.add(send.attributes.climb.climb_type as 'Boulder' | 'Route');
      }
    }
    return (['Boulder', 'Route'] as const).filter(t => types.has(t));
  }, [allSends, selectedLocationId]);

  useEffect(() => {
    if (availableTypes.length > 0 && !availableTypes.includes(climbType)) {
      setClimbType(availableTypes[0]);
    }
  }, [availableTypes, climbType]);

  const filteredSends = useMemo<Send[]>(() => {
    let base: Send[];
    if (selectedLocationId === ALL_GYMS_ID) {
      base = allSends.filter(send => {
        const c = send.attributes.climb;
        return c.official === true && c.climb_type === climbType;
      });
    } else if (!gym || !gradingSystem) {
      base = [];
    } else {
      base = allSends.filter(send => {
        const c = send.attributes.climb;
        return (
          c.location?.id === gym.id &&
          c.official === true &&
          c.climb_type === climbType &&
          c.grading_system_id === String(gradingSystem.id)
        );
      });
    }
    if (dateFrom) base = base.filter(s => s.attributes.sent_at.slice(0, 10) >= dateFrom);
    if (dateTo) base = base.filter(s => s.attributes.sent_at.slice(0, 10) <= dateTo);
    return base;
  }, [allSends, gym, climbType, gradingSystem, selectedLocationId, dateFrom, dateTo]);

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 px-4 py-6">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (allSends.length === 0) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 text-center text-muted-foreground">
        {t('stats.noSends')}
      </div>
    );
  }

  const isAllGyms = selectedLocationId === ALL_GYMS_ID;

  return (
    <div className="mx-auto max-w-2xl space-y-4 px-4 py-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
          <SelectTrigger className="w-full sm:w-60">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {locationOptions.map(loc => (
              <SelectItem key={loc.id} value={loc.id}>
                {loc.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {availableTypes.length > 1 && (
          <ToggleGroup
            type="single"
            value={climbType}
            onValueChange={(v) => { if (v) setClimbType(v as 'Boulder' | 'Route'); }}
          >
            {availableTypes.map(type => (
              <ToggleGroupItem key={type} value={type} className="px-4">
                {t(`stats.climbType.${type.toLowerCase()}`)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="shrink-0 text-sm text-muted-foreground">{t('stats.dateRange.from')}</span>
        <Input
          type="date"
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          className="h-9 w-auto"
        />
        <span className="shrink-0 text-sm text-muted-foreground">{t('stats.dateRange.to')}</span>
        <Input
          type="date"
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          className="h-9 w-auto"
        />
      </div>

      {gymLoading && (
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      )}

      {!gymLoading && filteredSends.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            {isAllGyms ? t('stats.noStyleData') : t('stats.noSendsForGym')}
          </CardContent>
        </Card>
      )}

      {!gymLoading && filteredSends.length > 0 && isAllGyms && (
        <AllGymsStats sends={filteredSends} />
      )}

      {!gymLoading && filteredSends.length > 0 && !isAllGyms && gym && (
        <GymUserStats gym={gym} climbType={climbType} sends={filteredSends} />
      )}
    </div>
  );
}
